import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, RefreshCcw } from 'lucide-react';
import { warehouses, warehouseItems, companySettings } from '../../services/api';
import WarehouseSelect from './WarehouseSelect';
import ItemSelect from './ItemSelect';
import { formatDateForCNB, calculateConversionRate } from '../../utils/currencyUtils';
import { fetchCNBExchangeRates } from '../../utils/currencyApi';

const MaterialReceiptModal = ({ isOpen, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [warehousesList, setWarehouses] = useState([]);
  const [itemsList, setItems] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [fetchingRate, setFetchingRate] = useState(false);
  const [exchangeRates, setExchangeRates] = useState(null);
  
  const [receiptData, setReceiptData] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    warehouseId: '',
    currency: '',
    conversionRate: 1,
    items: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (receiptData.date) {
      fetchExchangeRates();
    }
  }, [receiptData.date]);

  const fetchData = async () => {
    try {
      const [warehousesRes, itemsRes, companyRes] = await Promise.all([
        warehouses.getAll(),
        warehouseItems.getAll(),
        companySettings.get()
      ]);
      setWarehouses(warehousesRes.data);
      setItems(itemsRes.data);
      setCompanyData(companyRes.data);
      setReceiptData(prev => ({
        ...prev,
        currency: companyRes.data.defaultCurrency
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      setFetchingRate(true);
      const rates = await fetchCNBExchangeRates(receiptData.date);
      setExchangeRates(rates);
      
      // If we have a currency selected, update the conversion rate
      if (receiptData.currency && companyData?.defaultCurrency) {
        const newRate = calculateConversionRate(
          rates,
          receiptData.currency,
          companyData.defaultCurrency
        );
        updateConversionRate(newRate);
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      setError('Failed to fetch exchange rates. Please enter rate manually.');
    } finally {
      setFetchingRate(false);
    }
  };

  const updateConversionRate = (newRate) => {
    setReceiptData(prev => ({
      ...prev,
      conversionRate: newRate
    }));
    updateItemPrices(receiptData.items, newRate);
  };

  const updateItemPrices = (items, newRate) => {
    const updatedItems = items.map(item => {
      if (item.price && item.quantity) {
        return {
          ...item,
          priceInDefaultCurrency: item.price * newRate,
          totalPrice: item.price * item.quantity,
          totalPriceInDefaultCurrency: item.price * item.quantity * newRate
        };
      }
      return item;
    });
    
    setReceiptData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!receiptData.invoiceNumber) newErrors.invoiceNumber = 'Invoice number is required';
    if (!receiptData.warehouseId) newErrors.warehouseId = 'Warehouse is required';
    if (receiptData.items.length === 0) newErrors.items = 'At least one item is required';
    
    receiptData.items.forEach((item, index) => {
      if (!item.itemId) newErrors[`item-${index}`] = 'Select an item';
      if (!item.quantity || item.quantity <= 0) newErrors[`quantity-${index}`] = 'Enter valid quantity';
      if (!item.price || item.price <= 0) newErrors[`price-${index}`] = 'Enter valid price';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddItem = () => {
    setReceiptData({
      ...receiptData,
      items: [
        ...receiptData.items,
        { itemId: '', quantity: '', price: '', totalPrice: 0, priceInDefaultCurrency: 0, totalPriceInDefaultCurrency: 0 }
      ]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = receiptData.items.filter((_, i) => i !== index);
    setReceiptData({ ...receiptData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...receiptData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'price') {
      const quantity = field === 'quantity' ? Number(value) : Number(newItems[index].quantity);
      const price = field === 'price' ? Number(value) : Number(newItems[index].price);
      if (quantity && price) {
        newItems[index].totalPrice = quantity * price;
        newItems[index].priceInDefaultCurrency = price * receiptData.conversionRate;
        newItems[index].totalPriceInDefaultCurrency = quantity * price * receiptData.conversionRate;
      }
    }
    
    setReceiptData({ ...receiptData, items: newItems });
  };

  const handleCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setReceiptData(prev => ({
      ...prev,
      currency: newCurrency
    }));

    if (exchangeRates && companyData?.defaultCurrency) {
      try {
        const newRate = calculateConversionRate(
          exchangeRates,
          newCurrency,
          companyData.defaultCurrency
        );
        updateConversionRate(newRate);
      } catch (err) {
        setError('Failed to calculate conversion rate. Please enter manually.');
        console.error('Conversion calculation error:', err);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(receiptData);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">New Material Receipt</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Invoice Number *</label>
              <input
                type="text"
                className={`mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                  errors.invoiceNumber ? 'border-red-500' : ''
                }`}
                value={receiptData.invoiceNumber}
                onChange={(e) => setReceiptData({ ...receiptData, invoiceNumber: e.target.value })}
              />
              {errors.invoiceNumber && (
                <p className="mt-1 text-sm text-red-500">{errors.invoiceNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Date *</label>
              <input
                type="date"
                className="mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={receiptData.date}
                onChange={(e) => setReceiptData({ ...receiptData, date: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Currency *</label>
              <select
                className="mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={receiptData.currency}
                onChange={handleCurrencyChange}
              >
                {companyData?.enabledCurrencies
                  .filter(c => c.enabled)
                  .map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} ({currency.symbol})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Conversion Rate to {companyData?.defaultCurrency}
                <button
                  type="button"
                  onClick={fetchExchangeRates}
                  className="ml-2 text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  disabled={fetchingRate || receiptData.currency === companyData?.defaultCurrency}
                >
                  <RefreshCcw className={`h-4 w-4 ${fetchingRate ? 'animate-spin' : ''}`} />
                </button>
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                className="mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={receiptData.conversionRate}
                onChange={(e) => updateConversionRate(parseFloat(e.target.value))}
                disabled={receiptData.currency === companyData?.defaultCurrency}
              />
              {exchangeRates && (
                <p className="mt-1 text-xs text-gray-500">
                  CNB rate for {receiptData.date}: 1 {receiptData.currency} = {exchangeRates[receiptData.currency]?.toFixed(3)} CZK
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <WarehouseSelect
                warehouses={warehousesList}
                selectedWarehouse={receiptData.warehouseId}
                onChange={(warehouseId) => setReceiptData({ ...receiptData, warehouseId })}
              />
              {errors.warehouseId && (
                <p className="mt-1 text-sm text-red-500">{errors.warehouseId}</p>
              )}
            </div>
          </div>

          {/* Items Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-medium text-gray-900">Items</h4>
              <button
                type="button"
                onClick={handleAddItem}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </button>
            </div>

            {errors.items && (
              <p className="mb-4 text-sm text-red-500">{errors.items}</p>
            )}

            <div className="space-y-4">
              {receiptData.items.map((item, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="sm:col-span-2">
                      <ItemSelect
                        items={itemsList}
                        selectedItem={item.itemId}
                        onChange={(itemId) => handleItemChange(index, 'itemId', itemId)}
                      />
                      {errors[`item-${index}`] && (
                        <p className="mt-1 text-xs text-red-500">{errors[`item-${index}`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700">Quantity *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                          errors[`quantity-${index}`] ? 'border-red-500' : ''
                        }`}
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      />
                      {errors[`quantity-${index}`] && (
                        <p className="mt-1 text-xs text-red-500">{errors[`quantity-${index}`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700">Price per Unit *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                            errors[`price-${index}`] ? 'border-red-500' : ''
                        }`}
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                      />
                      {errors[`price-${index}`] && (
                        <p className="mt-1 text-xs text-red-500">{errors[`price-${index}`]}</p>
                      )}
                    </div>

                    {/* Show converted price if different from default currency */}
                    {receiptData.currency !== companyData?.defaultCurrency && (
                      <div className="sm:col-span-4 text-right">
                        <p className="text-sm text-gray-500">
                          Price in {companyData?.defaultCurrency}:{' '}
                          {(item.priceInDefaultCurrency || 0).toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Total in {companyData?.defaultCurrency}:{' '}
                          {(item.totalPriceInDefaultCurrency || 0).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="p-1 text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}

              {receiptData.items.length > 0 && (
                <div className="pt-4 border-t">
                  <div className="text-right space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total Amount: </span>
                      <span className="text-lg font-bold text-gray-900">
                        {receiptData.currency} {' '}
                        {receiptData.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toFixed(2)}
                      </span>
                    </div>

                    {receiptData.currency !== companyData?.defaultCurrency && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Total in {companyData?.defaultCurrency}: {' '}
                        </span>
                        <span className="text-base font-semibold text-gray-700">
                          {receiptData.items.reduce((sum, item) => sum + (item.totalPriceInDefaultCurrency || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {receiptData.items.length === 0 && (
                <div className="text-center py-4 text-sm text-gray-500">
                  No items added yet. Click "Add Item" to start.
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Create Receipt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialReceiptModal;